export interface PublicAnnouncement {
  readonly id: string;
  readonly title: string;
  readonly body: string;
  readonly severity: "info" | "warning" | "critical";
  readonly publishedAt: number;
}

export class AnnouncementClient {
  constructor(private readonly baseUrl: string, private readonly fetcher: typeof fetch = (input, init) => fetch(input, init)) {
    if (!/^https?:\/\//u.test(baseUrl)) throw new Error("公告服务地址无效");
  }

  async list(signal?: AbortSignal): Promise<readonly PublicAnnouncement[]> {
    let response: Response;
    try {
      response = await this.fetcher(`${this.baseUrl.replace(/\/$/u, "")}/announcements`, signal ? { signal } : undefined);
    } catch (cause) {
      if (cause instanceof DOMException && cause.name === "AbortError") throw cause;
      throw new Error("系统公告暂时无法读取");
    }
    if (!response.ok) throw new Error("系统公告暂时无法读取");
    return parseAnnouncements(await response.json());
  }
}

export function createAnnouncementClient(): AnnouncementClient | null {
  const baseUrl = import.meta.env.VITE_GAME_API_URL as string | undefined;
  return baseUrl ? new AnnouncementClient(baseUrl) : null;
}

export function parseAnnouncements(value: unknown): readonly PublicAnnouncement[] {
  const envelope = asRecord(value);
  if (!envelope || !Array.isArray(envelope.announcements) || envelope.announcements.length > 5) throw new Error("系统公告响应无效");
  const ids = new Set<string>();
  return envelope.announcements.map((item) => {
    const row = asRecord(item);
    if (!row || Object.keys(row).sort().join("\0") !== "body\0id\0publishedAt\0severity\0title"
      || typeof row.id !== "string" || row.id.length < 1 || row.id.length > 128 || ids.has(row.id)
      || typeof row.title !== "string" || row.title.length < 1 || row.title.length > 60
      || typeof row.body !== "string" || row.body.length < 1 || row.body.length > 2_000
      || row.severity !== "info" && row.severity !== "warning" && row.severity !== "critical"
      || typeof row.publishedAt !== "string" || !Number.isFinite(Date.parse(row.publishedAt))) {
      throw new Error("系统公告响应无效");
    }
    ids.add(row.id);
    return { id: row.id, title: row.title, body: row.body, severity: row.severity, publishedAt: Date.parse(row.publishedAt) };
  });
}

function asRecord(value: unknown): Readonly<Record<string, unknown>> | null {
  return typeof value === "object" && value !== null && !Array.isArray(value) ? value as Readonly<Record<string, unknown>> : null;
}
